import React, { useState, useMemo, useEffect } from 'react';
import { 
  X, 
  ShoppingBag, 
  CheckCircle2, 
  CheckCircle,
  AlertCircle, 
  Sparkles, 
  Plus, 
  Minus, 
  Check, 
  ShieldCheck, 
  Heart, 
  Share2, 
  Info, 
  ChevronDown, 
  ChevronUp,
  Star,
  MessageSquare,
  Send,
  User,
  MapPin
} from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useStore } from '../../context/StoreContext';
import { useCart } from '../../context/CartContext';
import { formatPrice, formatMizajDisplay } from '../../utils/formatters';
import { sortVariantsByWeight } from '../../utils/weightPricing';
import { getProductAltText } from '../../utils/seoHelpers';

const RATING_LABELS = {
  5: 'بہترین اور انتہائی مفید (5/5)',
  4: 'بہت اچھا رزلٹ (4/5)',
  3: 'مناسب و تسلی بخش (3/5)',
  2: 'عام نتائج (2/5)',
  1: 'مطمئن نہیں (1/5)'
};

const ProductDetailModal = () => {
  const { lang, t } = useLanguage();
  const { settings, mizajList, reviews, addReview, getProductReviews, getProductRatingStats, orders } = useStore();
  const { 
    selectedProductModal: product, 
    setSelectedProductModal, 
    addToCart 
  } = useCart();

  const mizajDisplay = formatMizajDisplay(product, mizajList, lang);

  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewForm, setReviewForm] = useState({
    customerName: '',
    city: '',
    orderId: '',
    rating: 5,
    comment: ''
  });
  const [hoverRating, setHoverRating] = useState(0);
  const [reviewSubmitted, setReviewSubmitted] = useState(false);

  // Auto-detect matching order if customer enters an Order ID
  const matchedOrder = useMemo(() => {
    if (!reviewForm.orderId || !orders || orders.length === 0) return null;
    const cleanId = reviewForm.orderId.trim().toUpperCase();
    return orders.find(o => o.id?.toUpperCase() === cleanId || o.id?.toUpperCase() === `HP-${cleanId}`);
  }, [reviewForm.orderId, orders]);

  const handleAutoFillFromOrder = () => {
    if (!matchedOrder) return;
    setReviewForm(prev => ({
      ...prev,
      customerName: matchedOrder.customerName || prev.customerName,
      city: matchedOrder.city || prev.city
    }));
  };

  // Compute live rating & reviews
  const ratingStats = useMemo(() => {
    if (!product) return { rating: 5.0, count: 0 };
    return getProductRatingStats ? getProductRatingStats(product.id, product.rating || 4.9, product.reviewsCount || 24) : {
      rating: product.rating || 4.9,
      count: product.reviewsCount || 24,
      userReviews: []
    };
  }, [product, reviews, getProductRatingStats]);

  const productReviews = useMemo(() => {
    if (!product) return [];
    return getProductReviews ? getProductReviews(product.id) : [];
  }, [product, reviews, getProductReviews]);

  const handleReviewSubmit = (e) => {
    e.preventDefault();
    if (!reviewForm.comment.trim() || !reviewForm.customerName.trim()) return;

    addReview({
      productId: product.id,
      customerName: reviewForm.customerName,
      city: reviewForm.city,
      orderId: reviewForm.orderId,
      rating: reviewForm.rating,
      comment: reviewForm.comment
    });

    setReviewSubmitted(true);
    setReviewForm({
      customerName: '',
      city: '',
      orderId: '',
      rating: 5,
      comment: ''
    });
    setTimeout(() => {
      setReviewSubmitted(false);
      setShowReviewForm(false);
    }, 2500);
  };

  const allImages = useMemo(() => {
    if (!product) return [];
    if (Array.isArray(product.images) && product.images.length > 0) {
      return product.images;
    }
    return product.image ? [product.image] : [];
  }, [product]);

  const [activeImageIndex, setActiveImageIndex] = useState(0);

  useEffect(() => {
    setActiveImageIndex(0);
  }, [product?.id]);

  // Sort variants ascending by weight (50g -> 100g -> 250g -> 500g -> 750g -> 1kg)
  const sortedVariants = useMemo(() => {
    return sortVariantsByWeight(product?.variants || []);
  }, [product]);

  const [selectedVariant, setSelectedVariant] = useState(null);

  useEffect(() => {
    if (sortedVariants.length > 0) {
      setSelectedVariant(prev => {
        if (!prev) return sortedVariants[0];
        const match = sortedVariants.find(v => v.weight === prev.weight);
        return match || sortedVariants[0];
      });
    } else {
      setSelectedVariant(null);
    }
  }, [sortedVariants]);

  const [quantity, setQuantity] = useState(1);
  const [addedAnimation, setAddedAnimation] = useState(false);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setSelectedProductModal(null);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [setSelectedProductModal]);

  // All hooks MUST be above this check
  if (!product) return null;

  const currentPrice = selectedVariant ? selectedVariant.price : (sortedVariants[0]?.price || 0);

  const handleAddToCart = () => {
    addToCart(product, selectedVariant, quantity);
    setAddedAnimation(true);
    setTimeout(() => setAddedAnimation(false), 1200);
  };

  return (
    <div 
      className="fixed inset-0 z-50 overflow-y-auto bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6 animate-fade-in cursor-pointer"
      onClick={() => setSelectedProductModal(null)}
    >
      <div 
        className="relative bg-white w-full max-w-3xl rounded-3xl shadow-2xl overflow-hidden border border-gray-100 my-8 cursor-default max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Prominent Close Button */}
        <button
          type="button"
          onClick={() => setSelectedProductModal(null)}
          className="absolute top-4 left-4 z-20 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/95 hover:bg-rose-600 text-gray-800 hover:text-white shadow-lg border border-gray-200 transition-all font-bold text-xs font-subheading cursor-pointer group"
          title="بند کریں (Close)"
        >
          <X className="w-4 h-4 group-hover:rotate-90 transition-transform duration-200" />
          <span>بند کریں</span>
        </button>

        {/* Scrollable Container */}
        <div className="overflow-y-auto flex-1">
          <div className="grid grid-cols-1 md:grid-cols-2">
            
            {/* Left: Product Image & Gallery */}
            <div className="relative bg-gray-50 p-4 sm:p-5 flex flex-col justify-between min-h-[320px]">
              <div className="relative aspect-square rounded-2xl overflow-hidden bg-white shadow-xs border border-gray-100 flex items-center justify-center">
                <img
                  src={allImages[activeImageIndex] || product.image}
                  alt={getProductAltText(product, activeImageIndex, lang)}
                  className="w-full h-full object-cover"
                />
                {/* Mizaj badge over image (پہلے عمومی گروپ پھر کامہ لگا کر درجہ بندی) */}
                {mizajDisplay && (
                  <div className="absolute bottom-3 right-3">
                    <span className="px-2.5 py-1 rounded-xl bg-black/80 backdrop-blur-md text-amber-300 text-xs font-bold font-urdu border border-white/20 shadow">
                      مزاج: {mizajDisplay}
                    </span>
                  </div>
                )}
              </div>

              {/* Thumbnails Gallery Strip (If multiple images exist) */}
              {allImages.length > 1 && (
                <div className="flex items-center gap-2 overflow-x-auto pt-3 pb-1 scrollbar-none">
                  {allImages.map((imgSrc, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setActiveImageIndex(idx)}
                      className={`w-14 h-14 sm:w-16 sm:h-16 rounded-xl overflow-hidden border-2 transition-all p-0.5 shrink-0 cursor-pointer ${
                        activeImageIndex === idx 
                          ? 'border-emerald-700 ring-2 ring-emerald-600/30 shadow-xs scale-105' 
                          : 'border-gray-200 hover:border-emerald-400 opacity-70 hover:opacity-100'
                      }`}
                    >
                      <img 
                        src={imgSrc} 
                        alt={getProductAltText(product, idx, lang)} 
                        className="w-full h-full object-cover rounded-lg"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Right: Tibb-e-Pakistani Clinical Details & Purchase Options */}
            <div className="p-6 sm:p-8 flex flex-col justify-between">
              <div>
                
                {/* Stock Status & Category */}
                <div className="flex items-center justify-between gap-2 mb-2">
                  <span className="px-2.5 py-1 rounded-md bg-pansar-50 text-pansar-800 text-xs font-semibold font-urdu">
                    دواخانہ طب پاکستانی
                  </span>
                  {product.inStock ? (
                    <span className="inline-flex items-center gap-1 text-emerald-600 text-xs font-bold font-urdu">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      <span>{t('inStock')}</span>
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-rose-600 text-xs font-bold font-urdu">
                      <AlertCircle className="w-3.5 h-3.5" />
                      <span>{t('outOfStock')}</span>
                    </span>
                  )}
                </div>

                {/* Titles */}
                <h2 className="text-xl sm:text-2xl font-black text-gray-900 font-title mb-1">
                  {lang === 'ur' ? product.nameUrdu : product.nameEn}
                </h2>
                <p className="text-xs text-gray-500 font-sans mb-2">
                  {lang === 'ur' ? product.nameEn : product.nameUrdu}
                </p>

                {/* Live Star Rating & Review count */}
                <div className="flex items-center gap-2 mb-4">
                  <div className="flex items-center text-amber-500">
                    <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                    <span className="font-bold text-xs text-gray-900 font-sans mr-1">{ratingStats.rating}</span>
                  </div>
                  <span className="text-xs text-gray-500 font-sans">
                    ({ratingStats.count} {lang === 'ur' ? 'ریویوز' : 'Reviews'})
                  </span>
                  <span className="text-gray-300">•</span>
                  <button
                    type="button"
                    onClick={() => setShowReviewForm(true)}
                    className="text-[11px] text-emerald-700 hover:text-emerald-800 font-bold underline font-urdu cursor-pointer"
                  >
                    اپنی رائے دیں
                  </button>
                </div>

              {/* Price Banner */}
              <div className="bg-pansar-50/80 rounded-2xl p-3.5 mb-5 border border-pansar-100 flex items-center justify-between">
                <div>
                  <span className="text-xs text-gray-500 font-urdu">{t('price')}</span>
                  <div className="text-2xl font-black text-pansar-900 font-sans">
                    {formatPrice(currentPrice * quantity, lang)}
                  </div>
                </div>
                {quantity > 1 && (
                  <span className="text-xs text-gray-500 font-urdu">
                    (فی یونٹ: {formatPrice(currentPrice, lang)})
                  </span>
                )}
              </div>

              {/* Weight / Packaging Variants */}
              {sortedVariants && sortedVariants.length > 0 && (
                <div className="mb-5">
                  <label className="block text-xs font-bold text-gray-700 font-urdu mb-2">
                    {t('selectWeight')}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {sortedVariants.map((v, idx) => {
                      const isSelected = selectedVariant?.weight === v.weight;
                      return (
                        <button
                          key={idx}
                          onClick={() => setSelectedVariant(v)}
                          className={`px-3 py-1.5 rounded-xl text-xs font-medium font-urdu border transition-all ${
                            isSelected
                              ? 'border-pansar-800 bg-pansar-800 text-white font-bold shadow-sm'
                              : 'border-gray-200 bg-gray-50 text-gray-700 hover:border-gray-300'
                          }`}
                        >
                          <span>{lang === 'ur' ? v.weight : (v.weightEn || v.weight)}</span>
                          <span className="mx-1 text-[11px] opacity-80">({formatPrice(v.price, lang)})</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Tabs / Accordions of Tibb-e-Pakistani Info */}
              <div className="space-y-3.5 text-xs text-gray-700 border-t border-gray-100 pt-4 mb-6 font-urdu">
                
                {/* 1. Benefits / افعال و خواص */}
                <div className="bg-emerald-50/60 rounded-xl p-3 border border-emerald-100">
                  <h4 className="font-bold text-emerald-900 mb-1 flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5 text-emerald-700" />
                    <span>{t('benefits')}</span>
                  </h4>
                  <p className="text-gray-700 leading-relaxed">
                    {lang === 'ur' ? product.benefitsUrdu : product.benefitsEn}
                  </p>
                </div>

                {/* 2. Usage & Dosage / طریقہ استعمال */}
                {product.usageUrdu && (
                  <div className="bg-amber-50/60 rounded-xl p-3 border border-amber-100">
                    <h4 className="font-bold text-amber-900 mb-1">
                      {t('usage')}
                    </h4>
                    <p className="text-gray-700 leading-relaxed">
                      {lang === 'ur' ? product.usageUrdu : product.usageEn}
                    </p>
                  </div>
                )}

                {/* 3. Precautions / احتیاطی تدابیر */}
                {product.precautionsUrdu && (
                  <div className="bg-rose-50/60 rounded-xl p-3 border border-rose-100">
                    <h4 className="font-bold text-rose-900 mb-1">
                      {t('precautions')}
                    </h4>
                    <p className="text-gray-700 leading-relaxed">
                      {lang === 'ur' ? product.precautionsUrdu : product.precautionsEn}
                    </p>
                  </div>
                )}

              </div>

            </div>

            {/* Bottom Actions: Quantity Selector & Buttons */}
            <div className="border-t border-gray-100 pt-4 space-y-3">
              
              <div className="flex items-center justify-between gap-4">
                <span className="text-xs font-bold text-gray-700 font-urdu">مقدار (Quantity):</span>
                <div className="flex items-center gap-2 border border-gray-200 rounded-xl p-1 bg-gray-50">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="p-1 rounded-lg hover:bg-white text-gray-700 transition"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="w-8 text-center font-bold text-sm font-sans">{quantity}</span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="p-1 rounded-lg hover:bg-white text-gray-700 transition"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="pt-2">
                <button
                  disabled={!product.inStock}
                  onClick={handleAddToCart}
                  className={`w-full py-3.5 px-6 rounded-2xl font-bold text-sm flex items-center justify-center gap-2.5 transition font-urdu ${
                    !product.inStock
                      ? 'bg-rose-50 text-rose-700 border border-rose-200 cursor-not-allowed opacity-90'
                      : addedAnimation
                      ? 'bg-emerald-600 text-white shadow-md transform scale-98 cursor-pointer'
                      : 'bg-pansar-800 hover:bg-pansar-900 text-white shadow-lg shadow-pansar-900/20 hover:shadow-xl cursor-pointer'
                  }`}
                >
                  {addedAnimation ? (
                    <>
                      <Check className="w-5 h-5" />
                      <span>کارٹ میں شامل کر دیا گیا ✓</span>
                    </>
                  ) : !product.inStock ? (
                    <>
                      <AlertCircle className="w-5 h-5 text-rose-500" />
                      <span>{lang === 'ur' ? 'یہ دوا فی الوقت دستیاب نہیں ہے (آؤٹ آف اسٹاک)' : 'Currently Out of Stock'}</span>
                    </>
                  ) : (
                    <>
                      <ShoppingBag className="w-5 h-5 text-amber-300" />
                      <span>{t('addToCart')} • {formatPrice(currentPrice * quantity, lang)}</span>
                    </>
                  )}
                </button>
              </div>

            </div>

          </div>
        </div>

        {/* Customer Reviews & Rating Section */}
          <div className="border-t border-gray-200 bg-gray-50/70 p-5 sm:p-8 font-urdu space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-gray-200/80 pb-4">
              <div>
                <div className="flex items-center gap-2">
                  <div className="flex items-center text-amber-500">
                    <Star className="w-5 h-5 fill-amber-400 text-amber-400" />
                    <span className="font-black text-xl text-gray-900 font-sans mr-1.5">{ratingStats.rating}</span>
                    <span className="text-xs text-gray-400 font-sans">/ 5.0</span>
                  </div>
                  <span className="text-xs text-emerald-700 bg-emerald-50 px-2.5 py-0.5 rounded-full font-bold border border-emerald-200">
                    {ratingStats.count} خریداروں کے مصدقہ ریویوز
                  </span>
                </div>
                <h3 className="font-bold text-base text-gray-900 mt-1">
                  کسٹمر ریٹنگز اور تصدیق شدہ خریداروں کی آراء
                </h3>
              </div>

              <button
                type="button"
                onClick={() => setShowReviewForm(!showReviewForm)}
                className="px-4 py-2 rounded-xl bg-pansar-800 hover:bg-pansar-900 text-white font-bold text-xs flex items-center justify-center gap-1.5 transition shadow-sm cursor-pointer"
              >
                <MessageSquare className="w-4 h-4 text-amber-300" />
                <span>{showReviewForm ? 'فارم بند کریں' : 'اپنی رائے درج کریں (ریویو لکھیں)'}</span>
              </button>
            </div>

            {/* Review Submission Form */}
            {showReviewForm && (
              <form onSubmit={handleReviewSubmit} className="bg-white rounded-2xl p-5 border border-emerald-200 shadow-sm space-y-4 animate-fade-in">
                <div className="flex items-center justify-between border-b border-gray-100 pb-2">
                  <h4 className="font-bold text-sm text-gray-900 flex items-center gap-1.5">
                    <Sparkles className="w-4 h-4 text-amber-500" />
                    <span>پروڈکٹ کے بارے میں اپنی رائے اور تجربہ شیئر کریں</span>
                  </h4>
                  <span className="text-[11px] text-gray-500">حکیم مولانا سید عبدالوہاب شاہ شیرازی دواخانہ</span>
                </div>

                {reviewSubmitted ? (
                  <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl text-center space-y-1">
                    <CheckCircle2 className="w-8 h-8 text-emerald-600 mx-auto" />
                    <h5 className="font-bold text-sm text-emerald-950">آپ کا ریویو کامیابی سے موصول ہو گیا ہے!</h5>
                    <p className="text-xs text-emerald-700">آپ کے تعاون اور اعتماد کا بہت شکریہ۔ آپ کی رائے ہمارے لیے اور دیگر مریضوں کے لیے قیمتی ہے۔</p>
                  </div>
                ) : (
                  <>
                    {/* Star Rating Selector */}
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1.5">
                        1. ریٹنگ منتخب کریں (اسٹار پر کلک کریں):
                      </label>
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <button
                              key={star}
                              type="button"
                              onClick={() => setReviewForm({ ...reviewForm, rating: star })}
                              onMouseEnter={() => setHoverRating(star)}
                              onMouseLeave={() => setHoverRating(0)}
                              className="p-1 text-gray-300 hover:scale-125 transition cursor-pointer"
                            >
                              <Star
                                className={`w-6 h-6 ${
                                  (hoverRating || reviewForm.rating) >= star
                                    ? 'fill-amber-400 text-amber-400'
                                    : 'text-gray-300'
                                }`}
                              />
                            </button>
                          ))}
                        </div>
                        <span className="text-xs font-bold text-amber-800 bg-amber-50 px-2.5 py-1 rounded-lg border border-amber-200">
                          {RATING_LABELS[hoverRating || reviewForm.rating]}
                        </span>
                      </div>
                    </div>

                    {/* Customer Inputs */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs font-bold text-gray-700 mb-1">
                          آپ کا نام <span className="text-rose-500">*</span>:
                        </label>
                        <input
                          type="text"
                          required
                          value={reviewForm.customerName}
                          onChange={(e) => setReviewForm({ ...reviewForm, customerName: e.target.value })}
                          placeholder="مثلاً: محمد احمد"
                          className="w-full p-2.5 rounded-xl border border-gray-200 text-xs focus:outline-none focus:border-pansar-700 bg-gray-50/50"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-gray-700 mb-1">
                          شہر (اختیاری):
                        </label>
                        <input
                          type="text"
                          value={reviewForm.city}
                          onChange={(e) => setReviewForm({ ...reviewForm, city: e.target.value })}
                          placeholder="مثلاً: اسلام آباد، لاہور"
                          className="w-full p-2.5 rounded-xl border border-gray-200 text-xs focus:outline-none focus:border-pansar-700 bg-gray-50/50"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-gray-700 mb-1">
                          آرڈر نمبر (اختیاری برائے تصدیق شدہ خریدار):
                        </label>
                        <input
                          type="text"
                          value={reviewForm.orderId}
                          onChange={(e) => setReviewForm({ ...reviewForm, orderId: e.target.value })}
                          placeholder="مثلاً: HP-9041"
                          className="w-full p-2.5 rounded-xl border border-gray-200 text-xs font-mono focus:outline-none focus:border-pansar-700 bg-gray-50/50 text-left dir-ltr"
                        />
                      </div>
                    </div>

                    {/* Matched Order Notification & Auto-fill */}
                    {matchedOrder && (
                      <div className="bg-emerald-50 p-3 rounded-xl border border-emerald-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-xs font-urdu text-emerald-900 animate-fade-in">
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                          <span>
                            تصدیق شدہ آرڈر مل گیا: <strong>{matchedOrder.customerName}</strong> ({matchedOrder.city}) — آپ کا ریویو <strong>"تصدیق شدہ خریدار"</strong> کے سبز بیج کے ساتھ شائع ہوگا!
                          </span>
                        </div>
                        {(!reviewForm.customerName || !reviewForm.city) && (
                          <button
                            type="button"
                            onClick={handleAutoFillFromOrder}
                            className="px-3 py-1 rounded-lg bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-[11px] shrink-0 cursor-pointer shadow-xs transition"
                          >
                            معلومات خودکار درج کریں ✓
                          </button>
                        )}
                      </div>
                    )}

                    {/* Comment */}
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1">
                        آپ کی تفصیلی رائے یا افاقہ کی تفصیل <span className="text-rose-500">*</span>:
                      </label>
                      <textarea
                        required
                        rows={3}
                        value={reviewForm.comment}
                        onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                        placeholder="اس دوا سے آپ کو کیا فائدہ ہوا؟ طریقہ استعمال اور معیار کے بارے میں اپنے خیالات لکھیں..."
                        className="w-full p-2.5 rounded-xl border border-gray-200 text-xs focus:outline-none focus:border-pansar-700 leading-relaxed"
                      />
                    </div>

                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => setShowReviewForm(false)}
                        className="px-4 py-2 rounded-xl text-xs font-bold text-gray-600 hover:bg-gray-100 transition cursor-pointer"
                      >
                        منسوخ کریں
                      </button>
                      <button
                        type="submit"
                        className="px-6 py-2.5 rounded-xl bg-emerald-700 hover:bg-emerald-600 text-white font-bold text-xs flex items-center gap-1.5 transition shadow cursor-pointer"
                      >
                        <Send className="w-3.5 h-3.5" />
                        <span>ریویو شائع کریں</span>
                      </button>
                    </div>
                  </>
                )}
              </form>
            )}

            {/* Existing Reviews List */}
            <div className="space-y-3">
              {productReviews.length === 0 ? (
                <div className="text-center py-6 bg-white rounded-2xl border border-gray-200/80 p-4">
                  <MessageSquare className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-xs text-gray-600 font-bold">
                    اس دوا پر ابھی آپ کی رائے کا انتظار ہے۔
                  </p>
                  <p className="text-[11px] text-gray-400 mt-0.5">
                    اگر آپ نے یہ دوا استعمال کی ہے تو "اپنی رائے درج کریں" پر کلک کر کے دیگر مریضوں کی رہنمائی فرمائیں۔
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {productReviews.map((rev) => (
                    <div key={rev.id} className="bg-white rounded-2xl p-4 border border-gray-200/80 shadow-xs space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-pansar-100 text-pansar-800 flex items-center justify-center font-bold text-xs">
                            {rev.customerName ? rev.customerName.charAt(0) : 'م'}
                          </div>
                          <div>
                            <div className="flex items-center gap-1.5">
                              <span className="font-bold text-xs text-gray-900">{rev.customerName}</span>
                              {rev.city && (
                                <span className="text-[10px] text-gray-400">({rev.city})</span>
                              )}
                            </div>
                            <span className="text-[10px] text-gray-400 block font-sans">{rev.date}</span>
                          </div>
                        </div>

                        <div className="flex items-center gap-1">
                          <div className="flex items-center text-amber-400">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                className={`w-3.5 h-3.5 ${
                                  i < rev.rating
                                    ? 'fill-amber-400 text-amber-400'
                                    : 'text-gray-200 fill-gray-100'
                                }`}
                              />
                            ))}
                          </div>
                        </div>
                      </div>

                      {rev.orderId && (
                        <div className="inline-flex items-center gap-1 text-[10px] text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full font-bold">
                          <CheckCircle className="w-3 h-3 text-emerald-600" />
                          <span>تصدیق شدہ خریدار (Verified Purchase)</span>
                        </div>
                      )}

                      <p className="text-xs text-gray-700 leading-relaxed pt-1 border-t border-gray-50">
                        {rev.comment}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

        </div>

      </div>
    </div>
  );
};

export default ProductDetailModal;
